/**
 * iurop-pharmacies.js — buscador + mapa de farmacias (Glucody).
 *
 * Dos modos:
 * - STOCKIST (data-pharm-stockist): carga las farmacias REALES del feed público
 *   de Stockist (382 a día de hoy, todas con coordenadas) y las pinta con
 *   nuestro diseño: tarjetas clonando <template data-pharm-tpl> y un pin por
 *   farmacia en Leaflet. Si el fetch falla → modo clásico.
 * - CLÁSICO: filtra las tarjetas Liquid ([data-pharm-card]) por texto y, si
 *   alguna trae data-pharm-coords, sustituye el iframe por Leaflet.
 *
 * ── Comportamiento pedido por el cliente (ago-2026) ──────────────────────────
 * 1) LA LISTA SIGUE AL MAPA. Antes mostraba las 12 primeras de las 382, fijas y
 *    sin relación con lo que se veía. Ahora, al mover o hacer zoom, la columna
 *    se repinta con las farmacias del encuadre, ordenadas por cercanía al
 *    centro del mapa. Tope de 20; el contador dice cuántas hay en la zona.
 * 2) EL PIN YA NO ABRE GOOGLE MAPS (se hizo así porque lo pidió el cliente en
 *    su día; ahora pide lo contrario). Selecciona la farmacia: sube la PRIMERA
 *    de la columna y se resalta. En escritorio abre además un popup sobre el
 *    pin; en móvil no, porque el mapa mide 280px y lo taparía entero.
 *    El botón "Cómo llegar" de la tarjeta sigue llevando a Google Maps.
 * 3) "USAR MI UBICACIÓN": centra el mapa en el visitante.
 *
 * ── El buscador NO filtra: MUEVE EL MAPA ─────────────────────────────────────
 * Antes era un filtro de texto sobre nombre+dirección+teléfono. Con la lista
 * siguiendo al mapa eso se contradecía solo: buscabas "Valencia", te ibas a
 * Madrid y leías "no hay farmacias en esta zona" con 34 resultados activos.
 * Ahora escribes un sitio → el mapa vuela allí → la lista muestra las de esa
 * zona. Es el modelo de cualquier localizador y encaja con lo anterior.
 *
 * El índice se construye en cliente desde el propio feed (ciudades, códigos
 * postales, provincias y nombres), sin API externa: sin clave, sin coste, sin
 * límite de peticiones y sin mandar a un tercero lo que teclea el visitante.
 * Se normaliza sin acentos y se casa por palabras sueltas, así "malaga"
 * encuentra "Málaga" y "farmacia gran via" funciona (antes ninguno de los dos).
 */
(function () {
  'use strict';

  var SHOW_DEFAULT = 12; // tarjetas antes de que exista el mapa
  var SHOW_IN_VIEW = 20; // tope de tarjetas dentro del encuadre
  var MAX_SUGG = { city: 4, zip: 4, prov: 2, shop: 3 };
  var STACKED = '(max-width: 989px)'; // por debajo el mapa va ARRIBA y la lista debajo

  // Provincia por los DOS primeros dígitos del código postal. Es tabla oficial
  // y estable, y por eso la usamos en vez del campo `state` del feed: ese trae
  // 60 valores distintos para 37 provincias reales (Valencia y VALENCIA por
  // separado, ALMERIA sin tilde, y un "TP" que es basura). Así el buscador es
  // inmune a cómo escriban los datos en Stockist.
  var PROVINCIAS = {
    '01': 'Álava', '02': 'Albacete', '03': 'Alicante', '04': 'Almería', '05': 'Ávila',
    '06': 'Badajoz', '07': 'Baleares', '08': 'Barcelona', '09': 'Burgos', '10': 'Cáceres',
    '11': 'Cádiz', '12': 'Castellón', '13': 'Ciudad Real', '14': 'Córdoba', '15': 'A Coruña',
    '16': 'Cuenca', '17': 'Girona', '18': 'Granada', '19': 'Guadalajara', '20': 'Gipuzkoa',
    '21': 'Huelva', '22': 'Huesca', '23': 'Jaén', '24': 'León', '25': 'Lleida',
    '26': 'La Rioja', '27': 'Lugo', '28': 'Madrid', '29': 'Málaga', '30': 'Murcia',
    '31': 'Navarra', '32': 'Ourense', '33': 'Asturias', '34': 'Palencia', '35': 'Las Palmas',
    '36': 'Pontevedra', '37': 'Salamanca', '38': 'Santa Cruz de Tenerife', '39': 'Cantabria',
    '40': 'Segovia', '41': 'Sevilla', '42': 'Soria', '43': 'Tarragona', '44': 'Teruel',
    '45': 'Toledo', '46': 'Valencia', '47': 'Valladolid', '48': 'Bizkaia', '49': 'Zamora',
    '50': 'Zaragoza', '51': 'Ceuta', '52': 'Melilla'
  };

  // Conectores que no se capitalizan al arreglar un "LAS PALMAS DE GRAN CANARIA".
  var MINUS = ['de', 'del', 'la', 'las', 'el', 'los', 'y', 'i', 'da', 'do', 'dos'];

  function isStacked() { return window.matchMedia(STACKED).matches; }

  // Sin acentos, sin signos y en minúsculas: es lo que hace que "malaga"
  // encuentre "Málaga" y "almeria" encuentre "ALMERÍA".
  // El rango de diacríticos va en escapes \uXXXX a propósito: escrito con los
  // caracteres combinantes literales el regex queda roto (el acento se combina
  // con el corchete) y los acentos NO se quitaban — probado.
  function norm(s) {
    return (s == null ? '' : String(s)).toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function titleCase(s) {
    return String(s).toLowerCase().split(/\s+/).map(function (w, i) {
      if (i > 0 && MINUS.indexOf(w) !== -1) return w;
      return w.charAt(0).toUpperCase() + w.slice(1);
    }).join(' ');
  }

  // Código postal español a 5 dígitos. En el feed hay un "2660" que en realidad
  // es 02660 (Albacete) sin el cero delante; sin rellenar se quedaba fuera.
  function zip5(s) {
    var d = String(s == null ? '' : s).replace(/\D/g, '');
    if (!d || d.length > 5) return '';
    while (d.length < 5) d = '0' + d;
    return d;
  }

  function loadLeaflet(cb) {
    if (window.L && window.L.map) return cb();
    var css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(css);
    var s = document.createElement('script');
    s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    s.onload = cb;
    document.head.appendChild(s);
  }

  function directionsUrl(it) {
    var dest = isFinite(it.lat) && isFinite(it.lng) ? it.lat + ',' + it.lng : it.address;
    return dest ? 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(dest) : '';
  }

  // Distancia real en km. Se usa tanto para ordenar por cercanía como para el
  // "a 1,2 km" de la tarjeta, así ordenación y texto nunca se contradicen.
  function km(a, b) {
    var R = 6371;
    var dLat = (b[0] - a[0]) * Math.PI / 180;
    var dLng = (b[1] - a[1]) * Math.PI / 180;
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(a[0] * Math.PI / 180) * Math.cos(b[0] * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
  }

  function centroid(points) {
    var la = 0, ln = 0;
    points.forEach(function (p) { la += p[0]; ln += p[1]; });
    return [la / points.length, ln / points.length];
  }

  // Mapa Leaflet sobre el canvas. Devuelve el mapa y helpers para (re)encuadrar.
  // El CSS (Leaflet + sección) llega async y el contenedor puede medirse
  // provisional → ResizeObserver re-encuadra hasta que el usuario toque.
  function makeMap(root, onReady) {
    var box = root.querySelector('[data-pharm-map]');
    var frame = root.querySelector('[data-pharm-iframe]');
    if (!box || box.dataset.mapInit) return;
    box.dataset.mapInit = 'true';

    loadLeaflet(function () {
      if (frame) frame.hidden = true;
      box.hidden = false;
      // El control de zoom se coloca a mano: en móvil va abajo a la derecha,
      // la zona cómoda del pulgar (arriba a la izquierda, su sitio por defecto,
      // es justo la esquina más incómoda de alcanzar con una mano).
      var map = L.map(box, { scrollWheelZoom: true, zoomControl: false });
      L.control.zoom({ position: isStacked() ? 'bottomright' : 'topleft' }).addTo(map);
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      }).addTo(map);

      var fitting = false;
      var userTouched = false;
      var target = []; // bounds objetivo actuales
      function fit(bounds, maxZoom) {
        if (bounds) target = bounds;
        if (!target.length) return;
        fitting = true;
        if (target.length === 1) map.setView(target[0], maxZoom || 14, { animate: false });
        else map.fitBounds(target, { padding: [40, 40], animate: false, maxZoom: maxZoom || 15 });
        fitting = false;
      }
      map.on('zoomstart dragstart', function () {
        if (!fitting) userTouched = true;
      });
      // La geolocalización y el buscador mueven el mapa por código: hay que
      // marcarlo como "tocado" a mano o el ResizeObserver lo devolvería a su
      // encuadre inicial.
      function lock() { userTouched = true; }

      if ('ResizeObserver' in window) {
        var lastW = box.clientWidth;
        var ro = new ResizeObserver(function () {
          if (userTouched) { ro.disconnect(); return; }
          var w = box.clientWidth;
          if (!w || w === lastW) return;
          lastW = w;
          map.invalidateSize();
          fit();
        });
        ro.observe(box);
      } else {
        setTimeout(function () { map.invalidateSize(); fit(); }, 400);
      }
      onReady({ map: map, fit: fit, lock: lock });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Modo STOCKIST                                                       */
  /* ------------------------------------------------------------------ */

  function stockistAddress(l) {
    var street = [(l.address_line_1 || '').trim(), (l.address_line_2 || '').trim()]
      .filter(Boolean).join(', ').replace(/,\s*$/, '');
    var town = [(l.postal_code || '').trim(), (l.city || '').trim()].filter(Boolean).join(' ');
    return [street, town].filter(Boolean).join(', ');
  }

  function initStockist(root, tag) {
    return fetch('https://stockist.co/api/v1/' + encodeURIComponent(tag) + '/locations/all')
      .then(function (r) {
        if (!r.ok) throw new Error('stockist ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var items = (data || []).map(function (l) {
          return {
            name: (l.name || '').trim(),
            address: stockistAddress(l),
            phone: (l.phone || '').trim(),
            city: (l.city || '').trim(),
            zip: zip5(l.postal_code),
            lat: parseFloat(l.latitude),
            lng: parseFloat(l.longitude)
          };
        }).filter(function (it) { return it.name; });
        if (!items.length) throw new Error('stockist empty');
        renderStockist(root, items);
      });
  }

  // Índice de sitios buscables a partir del feed: ciudades, códigos postales y
  // provincias. Una entrada = un grupo de farmacias, con su encuadre.
  function buildIndex(items) {
    var cities = {}, zips = {}, provs = {};

    function push(bag, key, label) {
      if (!bag[key]) bag[key] = { key: key, label: label, raw: [], items: [] };
      return bag[key];
    }

    items.forEach(function (it) {
      if (it.city) {
        var ck = norm(it.city);
        if (ck) { var c = push(cities, ck, it.city); c.raw.push(it.city); c.items.push(it); }
      }
      if (it.zip) {
        push(zips, it.zip, it.zip).items.push(it);
        var pn = PROVINCIAS[it.zip.slice(0, 2)];
        if (pn) push(provs, it.zip.slice(0, 2), pn).items.push(it);
      }
    });

    // "VALENCIA" y "Valencia" son la misma ciudad: nos quedamos con la variante
    // que el cliente escribió bien (la que tiene minúsculas) y, si todas están
    // en mayúsculas, la arreglamos nosotros. Sin esto salían duplicadas.
    Object.keys(cities).forEach(function (k) {
      var c = cities[k];
      var bonita = c.raw.filter(function (r) { return /[a-záéíóúñü]/.test(r); })[0];
      c.label = bonita || titleCase(c.raw[0]);
    });

    return { cities: cities, zips: zips, provs: provs };
  }

  function renderStockist(root, items) {
    var list = root.querySelector('.glucody-pharm__list');
    var tpl = root.querySelector('[data-pharm-tpl]');
    var none = root.querySelector('[data-pharm-none]');
    var count = root.querySelector('[data-pharm-count]');
    var input = root.querySelector('[data-pharm-search]');
    var sugg = root.querySelector('[data-pharm-sugg]');
    var msg = root.querySelector('[data-pharm-msg]');
    if (!list || !tpl) return;

    var txt = {
      cta: root.getAttribute('data-pharm-cta') || 'Cómo llegar',
      showing: root.getAttribute('data-pharm-i18n-showing') || 'Mostrando %shown% de %total% farmacias',
      inArea: root.getAttribute('data-pharm-i18n-in-area') || 'Mostrando %shown% de %total% farmacias en esta zona',
      noneArea: root.getAttribute('data-pharm-i18n-none-area') || 'No hay farmacias en esta zona.',
      gCities: root.getAttribute('data-pharm-i18n-g-cities') || 'Ciudades',
      gZips: root.getAttribute('data-pharm-i18n-g-zips') || 'Códigos postales',
      gProvs: root.getAttribute('data-pharm-i18n-g-provs') || 'Provincia',
      gShops: root.getAttribute('data-pharm-i18n-g-shops') || 'Farmacias',
      zipFallback: root.getAttribute('data-pharm-i18n-zip-fallback') || '',
      noMatch: root.getAttribute('data-pharm-i18n-no-match') || '',
      distKm: root.getAttribute('data-pharm-i18n-dist-km') || 'a %v km',
      distM: root.getAttribute('data-pharm-i18n-dist-m') || 'a %v m',
      noResults: none ? none.textContent : ''
    };
    var lang = document.documentElement.lang || 'es';

    // fuera las tarjetas Liquid (metaobjetos/bloques/fallback)
    list.querySelectorAll('[data-pharm-card]').forEach(function (c) { c.remove(); });

    items.forEach(function (it, i) {
      it.id = i;
      if (isFinite(it.lat) && isFinite(it.lng)) it.point = [it.lat, it.lng];
      it.hay = norm(it.name + ' ' + it.address); // para buscar por nombre
    });

    var index = buildIndex(items);
    var api = null;    // { map, fit, lock }
    var active = null; // farmacia seleccionada desde un pin
    var refPoint = null; // desde dónde se miden las distancias que se muestran

    function fmtDist(d) {
      if (d < 1) return txt.distM.replace('%v', Math.round(d * 1000));
      var v = d < 10 ? Math.round(d * 10) / 10 : Math.round(d);
      return txt.distKm.replace('%v', v.toLocaleString(lang, { maximumFractionDigits: 1 }));
    }

    function say(text, isError) {
      if (!msg) return;
      msg.textContent = text || '';
      msg.hidden = !text;
      msg.classList.toggle('is-error', !!isError);
    }

    function card(it) {
      var li = tpl.content.firstElementChild.cloneNode(true);
      li.dataset.pharmId = it.id;
      li.querySelector('.glucody-pharm__name').textContent = it.name;
      var dist = li.querySelector('[data-tpl-dist]');
      if (dist) {
        if (refPoint && it.point) { dist.textContent = fmtDist(km(refPoint, it.point)); dist.hidden = false; }
        else dist.remove();
      }
      var addr = li.querySelector('[data-tpl-address]');
      var phone = li.querySelector('[data-tpl-phone]');
      if (it.address) addr.querySelector('span').textContent = it.address;
      else addr.remove();
      if (it.phone) phone.querySelector('span').textContent = it.phone;
      else phone.remove();
      var cta = li.querySelector('.glucody-pharm__cta');
      var url = directionsUrl(it);
      if (url) { cta.href = url; cta.textContent = txt.cta; } else cta.remove();
      if (active && active.id === it.id) li.classList.add('is-active');
      return li;
    }

    function paint(rows, message, emptyMessage) {
      list.querySelectorAll('[data-pharm-card]').forEach(function (c) { c.remove(); });
      rows.forEach(function (it) { list.insertBefore(card(it), none); });
      if (none) {
        none.hidden = rows.length > 0;
        if (!rows.length) none.textContent = emptyMessage;
      }
      if (count) {
        count.hidden = rows.length === 0;
        count.textContent = message;
      }
    }

    // Lista = lo que hay DENTRO del encuadre actual, ordenado por cercanía al
    // centro del mapa. La farmacia seleccionada desde un pin va la primera.
    function renderList() {
      if (!api) {
        var head = items.slice(0, SHOW_DEFAULT);
        return paint(head,
          txt.showing.replace('%shown%', head.length).replace('%total%', items.length),
          txt.noResults);
      }
      var bounds = api.map.getBounds();
      var center = api.map.getCenter();
      var c = [center.lat, center.lng];

      var inView = items.filter(function (it) { return it.point && bounds.contains(it.point); });
      inView.forEach(function (it) { it._d = km(c, it.point); });
      inView.sort(function (a, b) { return a._d - b._d; });

      if (active && (!active.point || !bounds.contains(active.point))) active = null;
      if (active) inView = [active].concat(inView.filter(function (it) { return it.id !== active.id; }));

      var rows = inView.slice(0, SHOW_IN_VIEW);
      paint(rows,
        txt.inArea.replace('%shown%', rows.length).replace('%total%', inView.length),
        txt.noneArea);
    }

    function popupNode(it) {
      var w = document.createElement('div');
      w.className = 'glucody-pharm__pop';
      var h = document.createElement('strong');
      h.className = 'glucody-pharm__pop-name';
      h.textContent = it.name;
      w.appendChild(h);
      if (it.address) {
        var a = document.createElement('span');
        a.className = 'glucody-pharm__pop-line';
        a.textContent = it.address;
        w.appendChild(a);
      }
      if (it.phone) {
        var t = document.createElement('a');
        t.className = 'glucody-pharm__pop-line glucody-pharm__pop-tel';
        t.href = 'tel:' + it.phone.replace(/[^\d+]/g, '');
        t.textContent = it.phone;
        w.appendChild(t);
      }
      var url = directionsUrl(it);
      if (url) {
        var cta = document.createElement('a');
        cta.className = 'glucody-pharm__pop-cta';
        cta.target = '_blank';
        cta.rel = 'noopener';
        cta.href = url;
        cta.textContent = txt.cta;
        w.appendChild(cta);
      }
      return w;
    }

    function select(it) {
      active = it;
      renderList();
      var el = list.querySelector('[data-pharm-id="' + it.id + '"]');
      if (!el) return;
      // En móvil la lista está DEBAJO del mapa: hay que llevar al visitante
      // hasta ella o el cambio ocurre fuera de su pantalla y parece que el pin
      // no hace nada. La tarjeta se deja justo bajo el mapa pegajoso, que es lo
      // que hace el scroll-margin-top del CSS.
      el.scrollIntoView({ block: isStacked() ? 'start' : 'nearest', behavior: 'smooth' });
    }

    makeMap(root, function (mapApi) {
      api = mapApi;
      items.forEach(function (it) {
        if (!it.point) return;
        var marker = L.marker(it.point).addTo(api.map);
        marker.bindTooltip(it.name, { direction: 'top', offset: [0, -10] });
        // El ancho se acota AQUÍ (no por CSS): Leaflet mide el contenido y fija
        // el ancho en línea, y sobrescribirlo desde la hoja le descuadra el
        // centrado sobre la chincheta. autoPanPadding evita que el popup nazca
        // pegado al borde del mapa, que lo recorta por el border-radius.
        if (!isStacked()) {
          marker.bindPopup(popupNode(it), {
            closeButton: true, offset: [0, -8],
            minWidth: 220, maxWidth: 300, autoPanPadding: [24, 24]
          });
        }
        marker.on('click', function () { select(it); });
        it.marker = marker;
      });
      api.fit(items.filter(function (it) { return it.point; }).map(function (it) { return it.point; }));
      // Cada movimiento del mapa repinta la columna. moveend salta una sola vez
      // al terminar el gesto, así que no hace falta throttling.
      api.map.on('moveend', renderList);
      renderList();
      initGeo(root, api, function (p) { refPoint = p; });
    });

    /* -------------------- Buscador: sugerencias -------------------- */

    // Casa por PRINCIPIO DE PALABRA, no por subcadena suelta: así "val"
    // encuentra "Valencia" pero "31" deja de casar portales y teléfonos.
    function hitsWord(key, q) {
      return key.indexOf(q) === 0 || key.indexOf(' ' + q) !== -1;
    }

    function suggest(q) {
      var nq = norm(q);
      if (nq.length < 2) return [];
      var digits = /^\d+$/.test(nq);
      var out = [];

      if (!digits) {
        Object.keys(index.cities).filter(function (k) { return hitsWord(k, nq); })
          .sort(function (a, b) { return index.cities[b].items.length - index.cities[a].items.length; })
          .slice(0, MAX_SUGG.city)
          .forEach(function (k) { out.push({ type: 'city', group: txt.gCities, entry: index.cities[k] }); });

        Object.keys(index.provs).filter(function (k) { return hitsWord(norm(index.provs[k].label), nq); })
          .slice(0, MAX_SUGG.prov)
          .forEach(function (k) { out.push({ type: 'prov', group: txt.gProvs, entry: index.provs[k] }); });
      } else {
        Object.keys(index.zips).filter(function (k) { return k.indexOf(nq) === 0; })
          .sort().slice(0, MAX_SUGG.zip)
          .forEach(function (k) { out.push({ type: 'zip', group: txt.gZips, entry: index.zips[k] }); });

        // Nunca "0 resultados": un CP completo que no existe cae a su provincia.
        if (nq.length === 5 && !index.zips[nq]) {
          var pk = nq.slice(0, 2);
          if (index.provs[pk]) out.push({ type: 'zipfallback', group: txt.gProvs, entry: index.provs[pk], zip: nq });
        }
        // "31" es el PREFIJO de provincia tal cual (Navarra); zip5() aquí
        // rellenaría a 00031 y sacaría la provincia "00", que no existe.
        if (nq.length === 2 && index.provs[nq]) {
          if (!out.some(function (o) { return o.entry === index.provs[nq]; })) {
            out.push({ type: 'prov', group: txt.gProvs, entry: index.provs[nq] });
          }
        }
      }

      // Con consulta NUMÉRICA no buscamos por nombre/dirección: un "31" casa
      // números de portal y teléfonos, que es justo el ruido a evitar.
      if (!digits) {
        var words = nq.split(' ').filter(Boolean);
        items.filter(function (it) {
          return words.every(function (w) { return hitsWord(it.hay, w); });
        }).slice(0, MAX_SUGG.shop)
          .forEach(function (it) { out.push({ type: 'shop', group: txt.gShops, shop: it }); });
      }

      return out;
    }

    var current = [];
    var cursor = -1;

    function closeSugg() {
      if (!sugg) return;
      sugg.hidden = true;
      sugg.innerHTML = '';
      current = []; cursor = -1;
      if (input) { input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); }
    }

    function drawSugg(res) {
      if (!sugg) return;
      current = res; cursor = -1;
      sugg.innerHTML = '';
      if (!res.length) return closeSugg();
      var lastGroup = null;
      res.forEach(function (r, i) {
        if (r.group !== lastGroup) {
          lastGroup = r.group;
          var h = document.createElement('li');
          h.className = 'glucody-pharm__sugg-group';
          h.setAttribute('role', 'presentation');
          h.textContent = r.group;
          sugg.appendChild(h);
        }
        var li = document.createElement('li');
        li.className = 'glucody-pharm__sugg-item';
        li.id = 'pharm-sugg-' + i;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.dataset.i = i;
        var label = document.createElement('span');
        if (r.type === 'shop') label.textContent = r.shop.name;
        else if (r.type === 'zipfallback') label.textContent = r.zip + ' → ' + r.entry.label;
        else label.textContent = r.entry.label;
        li.appendChild(label);
        if (r.type !== 'shop') {
          var n = document.createElement('em');
          n.textContent = r.entry.items.length;
          li.appendChild(n);
        }
        sugg.appendChild(li);
      });
      sugg.hidden = false;
      if (input) input.setAttribute('aria-expanded', 'true');
    }

    function move(step) {
      var opts = Array.prototype.slice.call(sugg.querySelectorAll('.glucody-pharm__sugg-item'));
      if (!opts.length) return;
      cursor = (cursor + step + opts.length) % opts.length;
      opts.forEach(function (o, i) {
        o.classList.toggle('is-cursor', i === cursor);
        o.setAttribute('aria-selected', i === cursor ? 'true' : 'false');
      });
      opts[cursor].scrollIntoView({ block: 'nearest' });
      if (input) input.setAttribute('aria-activedescendant', opts[cursor].id);
    }

    function go(r) {
      if (!r || !api) return;
      api.lock();
      say('');
      if (r.type === 'shop') {
        active = r.shop;
        if (r.shop.point) { refPoint = r.shop.point; api.map.setView(r.shop.point, 16); }
        if (input) input.value = r.shop.name;
        closeSugg();
        if (r.shop.marker && !isStacked()) r.shop.marker.openPopup();
        return; // el moveend repinta y la deja la primera
      }
      var pts = r.entry.items.filter(function (it) { return it.point; }).map(function (it) { return it.point; });
      if (!pts.length) return;
      active = null;
      refPoint = centroid(pts);
      api.fit(pts, r.type === 'zip' ? 15 : 13);
      if (input) input.value = r.type === 'zipfallback' ? r.zip : r.entry.label;
      closeSugg();
      if (r.type === 'zipfallback' && txt.zipFallback) {
        say(txt.zipFallback.replace('%zip%', r.zip).replace('%province%', r.entry.label));
      }
      renderList();
    }

    if (input) {
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');
      if (sugg && sugg.id) input.setAttribute('aria-controls', sugg.id);

      input.addEventListener('input', function () {
        say('');
        var v = input.value.trim();
        if (!v) { closeSugg(); return; }
        drawSugg(suggest(v));
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') { e.preventDefault(); if (sugg.hidden) drawSugg(suggest(input.value)); else move(1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
        else if (e.key === 'Escape') { closeSugg(); }
        else if (e.key === 'Enter') {
          e.preventDefault();
          var r = cursor >= 0 ? current[cursor] : current[0];
          if (r) go(r);
          else if (input.value.trim() && txt.noMatch) {
            say(txt.noMatch.replace('%q%', input.value.trim()), true);
          }
        }
      });

      if (sugg) {
        sugg.addEventListener('mousedown', function (e) {
          var li = e.target.closest('.glucody-pharm__sugg-item');
          if (!li) return;
          e.preventDefault(); // no perder el foco antes del clic
          go(current[parseInt(li.dataset.i, 10)]);
        });
      }
      document.addEventListener('click', function (e) {
        if (sugg && !sugg.hidden && !e.target.closest('.glucody-pharm__tools')) closeSugg();
      });
    }

    renderList();
  }

  /* ------------------------------------------------------------------ */
  /* "Usar mi ubicación"                                                 */
  /* ------------------------------------------------------------------ */

  function initGeo(root, api, onLocated) {
    var btn = root.querySelector('[data-pharm-geo]');
    if (!btn) return;
    // Sin API de geolocalización (o sin HTTPS) el botón no puede funcionar:
    // mejor no enseñarlo que enseñarlo roto.
    if (!navigator.geolocation) { btn.hidden = true; return; }

    var msg = root.querySelector('[data-pharm-msg]');
    var label = btn.querySelector('span') || btn;
    var idle = label.textContent.trim();
    var t = {
      locating: root.getAttribute('data-pharm-i18n-locating') || 'Buscando tu ubicación…',
      error: root.getAttribute('data-pharm-i18n-geo-error') || '',
      denied: root.getAttribute('data-pharm-i18n-geo-denied') || ''
    };
    var here = null;
    var busy = false;

    function say(text) {
      if (!msg) return;
      msg.textContent = text || '';
      msg.hidden = !text;
      msg.classList.toggle('is-error', !!text);
    }

    btn.addEventListener('click', function () {
      if (busy) return;
      busy = true;
      btn.disabled = true;
      label.textContent = t.locating;
      say('');

      navigator.geolocation.getCurrentPosition(function (pos) {
        busy = false;
        btn.disabled = false;
        label.textContent = idle;
        var p = [pos.coords.latitude, pos.coords.longitude];
        api.lock();
        api.map.setView(p, 13);
        if (here) here.remove();
        here = L.circleMarker(p, {
          radius: 8, color: '#1f6aa6', weight: 2, fillColor: '#5aa9e6', fillOpacity: 0.9
        }).addTo(api.map);
        if (typeof onLocated === 'function') onLocated(p);
        // setView dispara moveend → la lista se repuebla sola con sus farmacias.
      }, function (err) {
        busy = false;
        btn.disabled = false;
        label.textContent = idle;
        say(err && err.code === 1 ? t.denied : t.error);
      }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Modo CLÁSICO (tarjetas Liquid: metaobjetos / bloques / fallback)    */
  /* ------------------------------------------------------------------ */

  function classicMap(root) {
    var points = [];
    root.querySelectorAll('[data-pharm-coords]').forEach(function (card) {
      var parts = (card.getAttribute('data-pharm-coords') || '').split(',');
      var lat = parseFloat(parts[0]);
      var lng = parseFloat(parts[1]);
      if (isNaN(lat) || isNaN(lng)) return;
      points.push({ lat: lat, lng: lng, title: card.getAttribute('data-pharm-title') || '' });
    });
    if (!points.length) return; // sin coordenadas → se queda el iframe

    makeMap(root, function (api) {
      var bounds = [];
      points.forEach(function (p) {
        var m = L.marker([p.lat, p.lng]).addTo(api.map);
        if (p.title) m.bindTooltip(p.title, { direction: 'top', offset: [0, -10] });
        bounds.push([p.lat, p.lng]);
      });
      api.fit(bounds);
      initGeo(root, api);
    });
  }

  function classicSearch(root) {
    var input = root.querySelector('[data-pharm-search]');
    if (!input) return;
    var cards = root.querySelectorAll('[data-pharm-card]');
    var none = root.querySelector('[data-pharm-none]');

    input.addEventListener('input', function () {
      var q = norm(input.value);
      var visible = 0;
      cards.forEach(function (card) {
        var match = norm(card.textContent).indexOf(q) !== -1;
        card.hidden = !match;
        if (match) visible++;
      });
      if (none) none.hidden = visible > 0;
    });
  }

  function init(root) {
    if (root.dataset.pharmInit) return;
    root.dataset.pharmInit = 'true';
    var tag = root.getAttribute('data-pharm-stockist');
    if (tag) {
      initStockist(root, tag).catch(function () {
        classicMap(root);
        classicSearch(root);
        // Ya no pintamos farmacias de muestra: si el feed cae y no hay tarjetas,
        // mostramos el estado vacío en lugar de una lista en blanco.
        if (!root.querySelector('[data-pharm-card]')) {
          var none = root.querySelector('[data-pharm-none]');
          if (none) none.hidden = false;
        }
      });
    } else {
      classicMap(root);
      classicSearch(root);
    }
  }

  function initAll() {
    document.querySelectorAll('[data-pharm]').forEach(init);
  }

  document.addEventListener('shopify:section:load', initAll);
  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);
})();
